/**
 * ISO 8601 week helpers.
 *
 * ISO 8601 rules:
 *  - Weeks start on Monday.
 *  - Week 1 of a year is the week that contains the first Thursday
 *    of that year (equivalently: the week containing January 4th).
 *  - Days in early January or late December may belong to a
 *    neighbouring year's week (e.g. 2027-01-01 is Fri and belongs to
 *    ISO week 53 of 2026; ISO week 1 of 2027 starts Mon 2027-01-04).
 *
 * Naive `Math.ceil((daysSinceJan1 + jan1.getDay() + 1) / 7)` returns
 * "1" for 2027-01-01 which is WRONG per ISO 8601. Use `isoWeekOfDate`
 * instead.
 */

// Returns { year, week } per ISO 8601 for the given JS Date.
export function isoWeekOfDate(d) {
  // Normalize to UTC midnight to avoid DST/timezone offsets.
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  // Shift to the Thursday of the current ISO week (day 4 of ISO week).
  const day = date.getUTCDay() || 7; // Sun=0 -> 7, Mon=1..Sat=6
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return { year: date.getUTCFullYear(), week: weekNo };
}

// Returns Monday of ISO week `w` for `year` as a UTC Date.
export function isoWeekStart(year, week) {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const week1Mon = new Date(jan4);
  week1Mon.setUTCDate(jan4.getUTCDate() - jan4Day + 1);
  const target = new Date(week1Mon);
  target.setUTCDate(week1Mon.getUTCDate() + (week - 1) * 7);
  return target;
}

// Number of ISO weeks in the given year (52 or 53).
export function weeksInYear(year) {
  // Dec 28th is always in the last ISO week of its own year.
  return isoWeekOfDate(new Date(year, 11, 28)).week;
}

/**
 * Convenience: given a Date (or YYYY-MM-DD string), returns the ISO
 * week label in the form "S{week}/{year}" — where `year` is the ISO
 * year (may differ from the calendar year for early Jan / late Dec).
 * Returns null if the input is falsy or invalid.
 *
 * Example: isoWeekLabel('2027-01-01') === 'S53/2026'
 *          isoWeekLabel('2027-01-04') === 'S1/2027'
 */
export function isoWeekLabel(input) {
  if (!input) return null;
  const d = input instanceof Date ? input : new Date(`${input}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const { year, week } = isoWeekOfDate(d);
  return `S${week}/${year}`;
}
